import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Chip,
  SelectChangeEvent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import api from '../services/api';

interface Organization {
  id: string;
  name: string;
  role: 'OWNER' | 'MANAGER' | 'MEMBER';
}

interface OrganizationSelectorProps {
  selectedOrganizationId?: string;
  onOrganizationChange: (organizationId: string) => void;
  userRole?: string;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  selectedOrganizationId,
  onOrganizationChange,
  userRole
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await api.getUserOrganizations();
        if (response.success) {
          // Transform the user organization data to match our interface
          const transformedOrgs = response.data.map((userOrg: any) => ({
            id: userOrg.organization.id,
            name: userOrg.organization.name,
            role: userOrg.role?.toUpperCase() || 'MEMBER' // Normalize to uppercase
          }));
          
          setOrganizations(transformedOrgs);
          
          // Auto-select first organization if none selected (but not if we have success parameters)
          const currentParams = new URLSearchParams(window.location.search);
          const hasSuccessParams = currentParams.get('success') === 'true' || currentParams.get('canceled') === 'true';
          
          if (!selectedOrganizationId && transformedOrgs.length > 0 && !hasSuccessParams) {
            onOrganizationChange(transformedOrgs[0].id);
          }
        } else {
          // Handle case where API returns success: false
          console.warn('Failed to fetch organizations:', response.message);
          setOrganizations([]);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        // Don't fail completely - just set empty organizations array
        setOrganizations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [selectedOrganizationId, onOrganizationChange]);

  const handleOrganizationChange = (event: SelectChangeEvent<string>) => {
    const organizationId = event.target.value;
    
    if (organizationId === 'create-new') {
      setOpenCreateDialog(true);
      return;
    }
    
    // Preserve success parameters when changing organizations
    const currentParams = new URLSearchParams(window.location.search);
    const success = currentParams.get('success');
    const canceled = currentParams.get('canceled');
    
    if (success === 'true' || canceled === 'true') {
      // Don't change organization if we have success parameters
      // This prevents overriding the success URL organization
      return;
    }
    
    onOrganizationChange(organizationId);
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim()) return;
    
    try {
      const response = await api.createOrganization({ name: newOrgName.trim() });
      if (response.success) {
        // Refresh organizations list
        const orgsResponse = await api.getUserOrganizations();
        if (orgsResponse.success) {
          const transformedOrgs = orgsResponse.data.map((userOrg: any) => ({
            id: userOrg.organization.id,
            name: userOrg.organization.name,
            role: userOrg.role?.toUpperCase() || 'MEMBER' // Normalize to uppercase
          }));
          setOrganizations(transformedOrgs);
          
          // Select the newly created organization
          onOrganizationChange(response.data.id);
        }
        
        setOpenCreateDialog(false);
        setNewOrgName('');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
    }
  };

  // const getSelectedOrganization = () => {
  //   return organizations.find(org => org.id === selectedOrganizationId);
  // };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'OWNER': return 'error';
      case 'MANAGER': return 'warning';
      case 'MEMBER': return 'default';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'OWNER': return 'Owner';
      case 'MANAGER': return 'Manager';
      case 'MEMBER': return 'Member';
      default: return role;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="inherit">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Always show dropdown (even for single org or no orgs)
  return (
    <>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={selectedOrganizationId || ''}
          onChange={handleOrganizationChange}
          displayEmpty
          sx={{ 
            color: 'inherit',
            '& .MuiSelect-icon': { color: 'inherit' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' }
          }}
        >
          {organizations.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No organizations available
              </Typography>
            </MenuItem>
          )}
          
          {organizations.map((org) => (
            <MenuItem key={org.id} value={org.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {org.name}
                </Typography>
                <Chip 
                  label={getRoleLabel(org.role)} 
                  size="small" 
                  color={getRoleColor(org.role) as any}
                  variant="outlined"
                />
              </Box>
            </MenuItem>
          ))}
          
          <Divider />
          <MenuItem value="create-new">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <AddIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Create New Organization
              </Typography>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>

      {/* Create Organization Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organization</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newOrgName.trim()) handleCreateOrganization();
          }}
        >
          <DialogContent>
            <TextField
              fullWidth
              label="Organization Name"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              margin="normal"
              autoFocus
              helperText="Enter a name for your new organization"
            />
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              onClick={() => {
                setOpenCreateDialog(false);
                setNewOrgName('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!newOrgName.trim()}
            >
              Create Organization
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default OrganizationSelector;
